/**
 * HomePage — ダッシュボード (FR-13/14/15)
 * 経験則 #62: ファーストビューは「次のアクション」を最上部、stat は下
 * 経験則 #63: カテゴリ正答率は色+ラベル+数値の三重符号化
 * 経験則 #21: フィルタ状態は URL searchParams SSOT
 */

import { useEffect, useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { getProgressOverview, getCategoryStats, getWeakAreas } from '../api'
import type { ProgressOverview, CategoryStat, WeakArea, NextActionType, CertificationType } from '../api/types'
import { AuthContext } from '../contexts/AuthContext'
import Icon from '../components/Icon'
import { PageLoader } from '../components/Spinner'

function scoreBand(ratio: number): { label: string; text: string; bar: string } {
  if (ratio >= 0.8) return { label: '得意', text: 'text-band-high', bar: 'bg-band-high' }
  if (ratio >= 0.6) return { label: 'あと少し', text: 'text-band-mid', bar: 'bg-band-mid' }
  return { label: '要強化', text: 'text-band-low', bar: 'bg-band-low' }
}

const ACTION_ICONS: Record<NextActionType, React.ComponentProps<typeof Icon>['name']> = {
  review_weak: 'refresh',
  mock_exam: 'clipboard',
  read_text: 'book',
}

export default function HomePage() {
  const { certType } = useContext(AuthContext)
  const navigate = useNavigate()
  const [overview, setOverview] = useState<ProgressOverview | null>(null)
  const [categoryStats, setCategoryStats] = useState<CategoryStat[]>([])
  const [weakAreas, setWeakAreas] = useState<WeakArea[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!certType) return
    const ct = certType as CertificationType
    setLoading(true)
    Promise.all([getProgressOverview(ct), getCategoryStats(ct), getWeakAreas(ct)])
      .then(([ov, cs, wa]) => {
        setOverview(ov)
        setCategoryStats(cs?.category_stats ?? [])
        setWeakAreas(wa?.weak_areas ?? [])
      })
      .finally(() => setLoading(false))
  }, [certType])

  const handleNextAction = (type?: NextActionType, categoryId?: string | null) => {
    if (!type) return
    if (type === 'review_weak') navigate('/review')
    else if (type === 'mock_exam') navigate('/mock-exam')
    else if (type === 'read_text') navigate(categoryId ? `/study?category_id=${categoryId}` : '/study')
  }

  if (!certType) {
    return (
      <div className="p-6 text-center text-text-secondary">
        <p>種別を選択してください</p>
        <button onClick={() => navigate('/cert-select')} className="btn-primary mt-4 px-6">
          種別を選択する
        </button>
      </div>
    )
  }

  if (loading) return <PageLoader />

  const pct =
    overview?.overall_correct_ratio != null ? Math.round(overview.overall_correct_ratio * 100) : null

  return (
    <div className="mx-auto max-w-content p-screen">
      {/* 次のアクション (#62 ファーストビュー最上部) */}
      {(overview?.next_actions?.length ?? 0) > 0 && (
        <section className="mb-6" data-testid="next-actions">
          <div className="mb-2.5 flex items-center gap-1.5 text-sm font-bold text-text-secondary">
            <Icon name="award" size={16} />
            次に取り組むこと
          </div>
          <div className="flex flex-col gap-2">
            {overview?.next_actions?.map((action, i) => (
              <div
                key={i}
                data-testid="next-action-item"
                className="flex items-center gap-3 rounded-lg border-l-4 border-accent bg-accent-subtle px-4 py-3.5"
              >
                <div className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-surface text-accent">
                  <Icon name={ACTION_ICONS[action.type ?? 'read_text'] ?? 'book'} size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-[15px] font-semibold text-text-primary">{action.label_ja}</span>
                </div>
                <button
                  data-testid="next-action-cta"
                  onClick={() => handleNextAction(action.type, action.category_id)}
                  className="inline-flex min-h-9 flex-none items-center rounded-md bg-accent px-3 py-2 text-[13px] font-semibold text-text-onaccent transition-colors hover:bg-accent-hover"
                >
                  始める
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Stat カード (next-action の下 #62) */}
      <section data-testid="stat-cards" className="mb-6">
        <div className="section-label">学習サマリ</div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: '総正答率', value: pct != null ? `${pct}%` : '—' },
            { label: '解答数', value: overview?.total_answered != null ? `${overview.total_answered}問` : '—' },
            { label: '模試回数', value: overview?.mock_exam_count != null ? `${overview.mock_exam_count}回` : '—' },
          ].map((s) => (
            <div key={s.label} className="card px-2 py-3 text-center">
              <div className="text-[11px] text-text-muted">{s.label}</div>
              <div className="mt-0.5 font-numeric text-[22px] font-bold leading-tight">{s.value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* カテゴリ別正答率 (#63 色+ラベル+数値) */}
      {categoryStats.length > 0 && (
        <section data-testid="category-progress-list" className="mb-6">
          <div className="section-label">カテゴリ別 正答率</div>
          <div className="overflow-hidden rounded-lg border border-border bg-surface">
            {categoryStats.map((cat, idx) => {
              const ratio = cat.correct_ratio ?? 0
              const band = scoreBand(ratio)
              return (
                <div
                  key={cat.category_id}
                  data-testid="category-progress-item"
                  className={idx < categoryStats.length - 1 ? 'border-b border-border px-4 py-3.5' : 'px-4 py-3.5'}
                >
                  <div className="mb-1.5 flex items-baseline justify-between">
                    <span className="text-sm font-semibold">{cat.category_name_ja}</span>
                    <span className={`text-xs ${band.text}`}>
                      {Math.round(ratio * 100)}% <span className="font-semibold">{band.label}</span>
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-pill bg-surface-alt">
                    <div
                      className={`h-full rounded-pill transition-[width] duration-300 ease-smooth ${band.bar}`}
                      style={{ width: `${Math.min(Math.round(ratio * 100), 100)}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* 苦手分野 (FR-15) */}
      {weakAreas.length > 0 && (
        <section data-testid="weak-areas" className="mb-6">
          <div className="section-label">苦手分野（正答率60%未満）</div>
          <div className="flex flex-col gap-2.5 rounded-lg border border-review bg-review-bg px-4 py-3.5">
            {weakAreas.map((wa) => (
              <div key={wa.category_id} className="flex items-center justify-between gap-2">
                <span className="text-sm text-text-primary">
                  {wa.category_name_ja}{' '}
                  <span className="font-bold text-review">{Math.round((wa.correct_ratio ?? 0) * 100)}%</span>
                </span>
                <button
                  onClick={() => navigate('/review')}
                  className="inline-flex items-center rounded-md bg-review px-3 py-1.5 text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
                >
                  まとめて復習
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
